import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Section, Eyebrow } from "@/components/PublicLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, HeartHandshake, Globe2, Sparkles, ArrowRight } from "lucide-react";

const KEYS = [["k12_parttime", "K–12 Accelerated — Part-Time"], ["k12_fulltime", "K–12 Accelerated — Full-Time"], ["bachelor", "Bachelor / Undergraduate"], ["master", "Master's"], ["doctorate", "Doctorate (PhD)"]];
const eur = (n) => `€${Number(n).toLocaleString()}`;

export default function Tuition() {
  const { lang } = useI18n();
  const fr = lang === "fr";
  const [data, setData] = useState(null);
  const [tkey, setTkey] = useState("bachelor");
  const [region, setRegion] = useState("west");
  const [allowAfrica, setAllowAfrica] = useState(false);
  useEffect(() => { api.get("/tuition").then((r) => setData(r.data)); }, []);
  useEffect(() => { api.get("/geo").then((r) => { const af = r.data.region === "africa"; setAllowAfrica(af); setRegion(af ? "africa" : "west"); }).catch(() => { setAllowAfrica(false); setRegion("west"); }); }, []);
  if (!data) return null;
  const row = data.matrix[tkey] || {};
  const band = row[region] || {};
  const inst = band.institutional;
  const MECH_ICON = { founding_award: Award, continuation_award: Sparkles, need_based: HeartHandshake, regional_access: Globe2 };

  return (
    <div data-testid="tuition-page">
      <Section className="py-16 md:py-24">
        <Eyebrow>{fr ? "Frais & accès" : "Tuition & access"}</Eyebrow>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tight text-ink">{fr ? "Des frais institutionnels, un accès intentionnel." : "Institutional tuition. Intentional access."}</h1>
        <p className="mt-5 max-w-2xl text-ink/65 leading-relaxed">{fr ? "Simo est une institution premium. Nous rendons l'excellence accessible par trois mécanismes distincts et honnêtes — jamais par des remises décoratives." : "Simo is a premium institution. We make excellence accessible through three distinct, honest mechanisms — never decorative discounts."}</p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Select value={tkey} onValueChange={setTkey}>
            <SelectTrigger data-testid="tuition-pathway" className="w-72 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{KEYS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="tuition-region-toggle" style={{ display: allowAfrica ? undefined : "none" }}>
            {[["west", fr ? "Global" : "Global"], ["africa", fr ? "Afrique" : "Africa"]].map(([r, l]) => (
              <button key={r} onClick={() => setRegion(r)} data-testid={`tuition-region-${r}`} className={`px-5 py-2 text-sm ${region === r ? "bg-ink text-paper" : "bg-transparent text-ink/60"}`}>{l}</button>
            ))}
          </div>
        </div>

        {/* Prestige tuition card */}
        <div className="mt-8 border border-border rounded-sm p-8 md:p-10 bg-secondary/40 max-w-3xl" data-testid="tuition-card">
          <div className="font-serif text-2xl text-ink">{row.label_en}</div>
          {inst == null ? (
            <div className="mt-4 text-ink/60">{band.note || (fr ? "Frais confirmés à l'admission." : "Tuition confirmed at admission.")}</div>
          ) : (
            <>
              <div className="mt-6">
                <div className="eyebrow text-ink/40">{fr ? "Frais institutionnels" : "Institutional tuition"}</div>
                <div className="font-serif text-6xl text-ink mt-1" data-testid="tuition-institutional">{eur(inst)}<span className="text-xl text-ink/40"> / {fr ? "an académique" : "academic year"}</span></div>
                <p className="mt-2 text-sm text-ink/55 max-w-xl">{fr ? "Petites cohortes, enseignement direct, évaluation par la maîtrise, progression individualisée et préparation aux qualifications." : "Small cohorts, direct faculty instruction, mastery assessment, individualised progression and qualification-pathway preparation."}</p>
              </div>

              {region === "west" ? (
                <div className="mt-6 border-t border-border pt-6">
                  {band.founding_award_max > 0 && (
                    <div className="text-ink/75"><span className="text-heraldic font-medium">{fr ? "Bourses de la Cohorte Fondatrice" : "Founding Intake Awards"}:</span> {fr ? `bourses jusqu'à ${eur(band.founding_award_max)} actuellement disponibles pour les étudiants éligibles admis à la cohorte 2026–2027.` : `scholarships of up to ${eur(band.founding_award_max)} currently available to eligible students admitted to the 2026–2027 intake.`}</div>
                  )}
                  {band.typical_net != null && (
                    <div className="mt-2 text-ink" data-testid="tuition-typical-net">{fr ? "Frais typiques après bourse de cohorte fondatrice :" : "Typical tuition after a Founding Intake Award:"} <b>{fr ? `à partir de ${eur(band.typical_net)}/an` : `from ${eur(band.typical_net)}/year`}</b></div>
                  )}
                  {band.continuation_award > 0 && (
                    <div className="mt-2 text-sm text-ink/60">{fr ? "Familles fondatrices : une Bourse de Continuité peut s'appliquer." : "Founding families: a Continuation Award may apply."}</div>
                  )}
                </div>
              ) : (
                <div className="mt-6 border-t border-border pt-6">
                  <div className="eyebrow text-heraldic">{fr ? "Frais d'Accès Régionaux — Afrique" : "Regional Access Tuition — Africa"}</div>
                  <div className="font-serif text-5xl text-ink mt-1" data-testid="tuition-regional-access">{band.regional_access != null ? eur(band.regional_access) : "—"}<span className="text-lg text-ink/40"> / {fr ? "an" : "year"}</span></div>
                  <p className="mt-2 text-sm text-ink/55 max-w-xl">{fr ? "Des frais intentionnels, fixés par l'institution pour les étudiants résidant et étudiant depuis l'Afrique — non une remise temporaire." : "An intentional, institution-set tuition for students residing and studying from Africa — not a temporary discount."}</p>
                  {band.instalment && <div className="mt-2 text-sm text-ink/60">{fr ? "Échelonnement" : "Instalments"}: {band.instalment}</div>}
                </div>
              )}

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/apply" data-testid="tuition-apply" className="inline-flex items-center gap-2 bg-heraldic text-white px-6 py-3 rounded-sm hover:bg-heraldic-600 transition-colors">{fr ? "Candidater — 97 €" : "Apply for admission — €97"} <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/financial-aid" data-testid="tuition-aid-link" className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-sm hover:bg-secondary transition-colors">{fr ? "Aide financière selon les besoins" : "Apply for need-based aid"}</Link>
              </div>
              <p className="mt-3 text-xs text-ink/45 max-w-xl">{fr ? data.policy.reassurance_fr : data.policy.reassurance_en}</p>
            </>
          )}
        </div>
      </Section>

      {/* The three mechanisms */}
      <div className="bg-ink text-paper">
        <Section className="py-16 md:py-20">
          <Eyebrow>{fr ? "Trois mécanismes, distincts et honnêtes" : "Three mechanisms, distinct and honest"}</Eyebrow>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="tuition-mechanisms">
            {data.policy.mechanisms.map((m) => { const Ic = MECH_ICON[m.key] || Award; return (
              <div key={m.key} className="border border-white/10 rounded-sm p-6">
                <Ic className="h-6 w-6 text-heraldic" />
                <h3 className="mt-4 font-serif text-lg">{fr ? m.name_fr : m.name_en}</h3>
                <p className="mt-2 text-sm text-paper/60 leading-relaxed">{fr ? m.desc_fr : m.desc_en}</p>
                {m.key === "need_based" && <Link to="/financial-aid" className="mt-3 inline-flex text-heraldic text-sm">{fr ? "Faire une demande" : "Apply"} →</Link>}
              </div>
            ); })}
          </div>
        </Section>
      </div>

      {/* Full institutional matrix */}
      <Section className="py-16">
        <div className="eyebrow text-ink/40 mb-4">{fr ? "Grille institutionnelle" : "Institutional schedule"}</div>
        <div className="overflow-x-auto border border-border rounded-sm">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-ink/60"><tr>
              <th className="text-left p-3 font-medium">{fr ? "Programme" : "Programme"}</th>
              <th className="text-right p-3 font-medium">{fr ? "Frais institutionnels" : "Institutional tuition"}</th>
              <th className="text-right p-3 font-medium">{region === "africa" ? (fr ? "Accès régional — Afrique" : "Regional Access — Africa") : (fr ? "Typique après bourse" : "Typical after award")}</th>
            </tr></thead>
            <tbody>
              {Object.entries(data.matrix).map(([k, v]) => {
                const b = v[region] || {};
                const right = region === "africa" ? b.regional_access : b.typical_net;
                return (
                  <tr key={k} className="border-t border-border">
                    <td className="p-3 text-ink">{v.label_en}</td>
                    <td className="p-3 text-right font-mono">{b.institutional != null ? eur(b.institutional) : "TBC"}</td>
                    <td className="p-3 text-right font-mono text-heraldic">{right != null ? eur(right) : (b.institutional == null ? "TBC" : "—")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-ink/45 max-w-3xl">{fr ? "Les frais sont indicatifs et confirmés à l'admission. Les bourses et l'aide sont décidées avec l'admission." : "Figures are indicative and confirmed at admission. Awards and aid are decided together with admission."}</p>
      </Section>
    </div>
  );
}
